/**
 * Plugin to apply, bind and handle any events and fixes for the navigation.
 */

import $ from 'jquery';

import App from 'libs/app';

import { Foundation } from 'foundation-sites/js/foundation.core';
import { transitionend } from 'foundation-sites/js/foundation.core.utils';
import { MediaQuery } from 'foundation-sites/js/foundation.util.mediaQuery';
import 'jquery-ui/ui/widget';

$.widget('tud.navigation', {
    /**
     * Properties
     */
    options: {
        // mainContainer:      'main',
        animationTime: 200,
        // animationStartTime: 0,
        // delayTime:          200,
        // largeUp:            769,
        initialHeight: 0,
        mobileBurgerSize: 'medium',
        lang: $('html').attr('lang'),
        debug: false,
    },
    // the pixel threshold from which on the header goes thin and in light mode
    lightHeaderThreshold: 50,
    // whether the user scrolled (or is at the top of the page)
    viewportScrolled: false,
    /**
     * Constructor
     *
     * @private
     */
    _create() {
        this.viewportScrolled = (window.scrollY || document.documentElement.scrollTop) > this.lightHeaderThreshold;

        this._initBackToTopButton();
        this._initHeaderNavigation();
        this._initHeaderPanelListeners();
        this._initFlyoutScreenReadability();
        this._isMobile.bind(this);
    },

    /**
     * Helper to check if a given size or the current size is mobile.
     * @param {Number} size=null - the given size to check or null to check the current viewport size
     * @returns {Boolean}
     */
    _isMobile(size = null) {
        if (size === null) {
            return MediaQuery.upTo(this.options.mobileBurgerSize);
        }
        const breakpoints = MediaQuery.queries.map((breakpoint) => breakpoint.name);
        return breakpoints.indexOf(size) <= breakpoints.indexOf(this.options.mobileBurgerSize);
    },

    /**
     * Bind click event handler to back to top link for scroll animation
     *
     * @private
     */
    _initBackToTopButton() {
        const { options } = this;
        $('#go-to-top-link').click((e) => {
            e.preventDefault();
            $('html, body').animate({ scrollTop: 0 }, options.animationTime);
        });
    },

    /**
     * Trim breadcrumb navigation
     *
     * @private
     */
    _trimBreadcrumbNavigation() {
        const breadcrumbs = document.getElementById('nav-breadcrumbs');
        // no breadcrumb container
        if (!breadcrumbs) return;
        const ulList = breadcrumbs.getElementsByTagName('ul');
        // no breadcrumb list
        if (ulList.length === 0) return;
        const ul = ulList[0];
        const li = ul.getElementsByTagName('li');

        for (let item of li) {
            // reset the li classes
            item.classList.remove('crumb-hidden');
            item.classList.remove('crumb-ellipsis');
        }

        // breadcrumbs with 3 or less items will always fit
        if (li.length < 4) return;
        // check ul for height and reduce li until the ul fits its height
        // calculate the indexes of the li to hide:
        // positions: 0 (home), -2 (parent), -1 (current) will always stay visible
        // hide 3 to -3, then 2, then -2, then 1
        let addedEllipsis = false;
        const indexes = Array.from({ length: li.length - 5 }, (_, v) => v + 3);
        indexes.push(2);
        if (li.length > 4) indexes.push(li.length - 2);
        indexes.push(1);
        const checkIndexes = () => {
            for (let index of indexes) {
                if (ul.clientHeight >= ul.scrollHeight) break;
                // make sure the item exists
                if (li[index] === undefined) continue;
                // mark item at index
                li[index].classList.add('crumb-hidden');
                // the first item to vanish shows an ellipsis instead
                if (!addedEllipsis) {
                    addedEllipsis = true;
                    li[index].classList.add('crumb-ellipsis');
                }
            }
        }
        checkIndexes();
        // recheck the height after a short timeout (sometimes recalc is too slow)
        window.setTimeout(checkIndexes, 100);
    },

    /**
     * Reorder the variable header items for mobile / desktop
     * @param {boolean} mobile true for mobile, false for desktop
     */
    _orderVariableHeader(mobile) {
        // get the variable header container wrapper
        const headerContainer = document.getElementById('variable-header-container')?.querySelector('.wrapper');
        if (!headerContainer) return;
        const child1 = headerContainer.querySelector('.item-targetgroups');
        const child2 = headerContainer.querySelector('.item-shortcuts');
        if (!child1 || !child2) return;
        if (mobile) {
            headerContainer.insertBefore(child2, child1);
        } else {
            headerContainer.insertBefore(child1, child2);
        }
    },

    /**
     * Bind click event handler for Search button
     * Switch visibilities of mobile and/or desktop burger panels according to the screen size
     * and scroll state.
     * "full header mode" is the header state on desktop on top of the page,
     * "light header mode" is the header state on mobile or if page is scrolled
     * "thin header" is activated if the page is scolled
     *
     * Note that the full header burger panel ("navigation-panel") is nested inside
     * the light header burger panel ("variable-header-container")!
     * Their visibilities get switched here accordingly.
     *
     * @private
     */
    _initHeaderNavigation() {
        // relevant DOM elements or attributes we need
        const buttonFull = document.querySelector('#navigation-panel-button button');
        const buttonLight = document.querySelector('#variable-header-container-button button');
        const buttonSearch = document.querySelector('#navigation-search-button button');
        const $panelFull = this._findFlyoutForLink($(buttonFull));
        const $panelLight = this._findFlyoutForLink($(buttonLight));
        const $panelSearch = this._findFlyoutForLink($(buttonSearch));
        const headerClasses = document.getElementById('header').classList;

        /**
         * Activate the light header mode
         * @param {any} onload=false - whether we activate the mobile mode in a site load situation
         */
        const activateLightHeader = (onload = false) => {
            headerClasses.remove('full-header');
            headerClasses.add('light-header');

            // desktop panel resides inside the mobile burger so deactivate close on click
            const panelFullToggle = $panelFull.data('zfPlugin'); // plugin data should be available now
            panelFullToggle.options.closeOnClick = false;

            // let light header burger close on click again
            const panelLightToggle = $panelLight.data('zfPlugin'); // plugin data should be available now
            panelLightToggle.options.closeOnClick = true;

            // order variable header items for mobile
            this._orderVariableHeader(true);

            // Test if the desktop burger is closed, otherwise do nothing, as the full burger should then be visible on
            // mobile, too.
            if (buttonFull.getAttribute('aria-expanded') === 'false') {
                // The desktop burger content is placed inside the mobile burger in mobile mode, so we have to
                // make it visible if it is not already.
                $panelFull.foundation('show');

                // If the screen size changes from desktop to mobile while the page is already open,
                // the mobile burger is visible, so we have to hide it.
                const searchActive = buttonSearch.getAttribute('aria-expanded') === 'true';
                if (searchActive) {
                    // activate the search panel again
                    $panelLight.foundation('show');
                    $panelLight.find('input').eq(0).focus();
                } else {
                    $panelLight.foundation('hide');
                }
            } else {
                // initially, $panelLight is hidden by CSS on mobile screens, so we have to show it now
                $panelLight.foundation('show');
            }
            this._toggleHeaderIconsVisibility(true);
            this._trimBreadcrumbNavigation();
        };

        /**
         * Activate the full header mode
         * @param {any} onload=false - whether we activate the mobile mode in a site load situation
         */
        const activateFullHeader = (onload = false) => {
            headerClasses.add('full-header');
            headerClasses.remove('light-header');

            const panelFullToggle = $panelFull.data('zfPlugin'); // plugin data should be available now
            // let full header burger close on click again
            panelFullToggle.options.closeOnClick = true;

            const panelLightToggle = $panelLight.data('zfPlugin'); // plugin data should be available now
            // deactivate close on click for light header burger
            panelLightToggle.options.closeOnClick = false;

            // order variable header items for desktop
            this._orderVariableHeader(false);

            // Test if the mobile burger is open, otherwise do nothing, as the burger and other header navigation
            // should then be visible on desktop, too.
            if (buttonLight.getAttribute('aria-expanded') === 'false') {
                // If the screen size changes from mobile to desktop while the page is already open,
                // the desktop burger is visible, so we have to hide it.
                if (!onload) {
                    $panelFull.foundation('hide');
                }

                // The navigation dropdowns of the desktop header are hidden in the mobile burger, so we have to
                // show it now, but without blue.
                $panelLight.foundation('show');
            } else if (buttonSearch.getAttribute('aria-expanded') === 'true') {
                // activate the search panel again
                $panelFull.foundation('hide');
                $panelSearch.foundation('show');
                this._enableBlur();
                $panelSearch.find('input').eq(0).focus();
            } else if (!onload) {
                // need to disable the header icons when full header was activated
                // with expanded burger (hence not onload) but without search
                this._toggleHeaderIconsVisibility(false);
            }
            this._trimBreadcrumbNavigation();
        };

        // on screen size changes, set the correct burger visibilities
        $(window).on('changed.zf.mediaquery', (event, newSize, oldSize) => {
            if (this._isMobile(oldSize) === this._isMobile(newSize)) {
                return;
            }

            if (this._isMobile(newSize)) {
                // viewport changed to mobile
                activateLightHeader();
            } else {
                // viewport changed to desktop
                activateFullHeader();
            }
        });

        /**
         * Set or remove the "thin" header class according to the scroll state
         */
        const refreshHeaderThickness = () => {
            if (this.viewportScrolled) {
                headerClasses.add('thin-header');
            } else {
                headerClasses.remove('thin-header');
            }
        };

        /**
         * Callback function for the scroll event - switches between full/light
         */
        const handleScroll = () => {
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;

            // do nothing if the scroll is between half the threshold and the threshold to prevent header "shaking"
            if (scrollPosition > this.lightHeaderThreshold / 2 && scrollPosition <= this.lightHeaderThreshold) {
                return;
            }

            const oldViewportScrolled = this.viewportScrolled;
            this.viewportScrolled = scrollPosition > this.lightHeaderThreshold;

            if (oldViewportScrolled === this.viewportScrolled) {
                // nothing changed
                return;
            }

            refreshHeaderThickness();
        };

        // listen for scroll events (and debounce them)
        document.addEventListener('scroll', Foundation.util.throttle((e) => {
            if (!document.body.classList.contains('blur-active')) {
                handleScroll();
            }
        }, 100), { passive: true });

        // Initially set the correct burger visibilities.
        // If a panel is not initialized by Foundation, wait for it.
        const panels = [$panelFull, $panelLight];
        let notInitialized = panels.length;

        /**
         * Try to set mobile or desktop mode, but only if all panels are initialized by foundation yet
         */
        const tryInit = () => {
            if (notInitialized > 0) {
                return;
            }

            // all panels are initialized, actually set the correct mode
            if (!MediaQuery.upTo(this.options.mobileBurgerSize)) {
                activateFullHeader(true);
            } else {
                activateLightHeader(true);
            }

            refreshHeaderThickness();
        };

        panels.forEach(($panel) => {
            if ($panel.data('zfPlugin')) {
                // panel is already initialized by Foundation
                notInitialized -= 1;
                // no tryInit here, it get's called after the forEach
            } else {
                // panel is not yet initialized, wait for Foundation's init event
                $panel.one('init.zf.toggler', () => {
                    notInitialized -= 1;
                    // when the uninitialized panels were initialized, tryInit will run successfully on the last one
                    tryInit();
                });
            }
        });
        // this tryInit will not run, if panels were uninitialized
        tryInit();
    },

    /**
     * On/Off-switch for the header icons visibility
     * They need to be switched off, when the burger opens and back on, when it closes.
     * The thin header (sticky) uses css to do the same.
     * @param {*} on
     */
    _toggleHeaderIconsVisibility(on = true) {
        const $container = $('#variable-header-container');
        const $items = $('.item-shortcuts, .item-targetgroups, .item-language', $container);
        if (on) {
            $items.show();
        } else {
            $items.hide();
        }
    },

    /**
     * Sets various event listeners on header panels (navigation desktop / mobile, search)
     * to handle blur background, sync states and prevent closing/opening in certain cases
     *
     * Note that the events from animation/toggle events can bubble from nested panels,
     * that's why we check that they were actually triggered by one of the nav panels,
     * e.g.: this === event.target
     *
     * @private
     */
    _initHeaderPanelListeners() {
        const context = this;

        // enable blur when burger gets opened, disable when closed
        // need this for desktop (navigation-panel) and mobile (variable-header-container) for scroll deactivation
        $('#navigation-panel, #variable-header-container').on('animateIn.zf.toggler.blur', function(event) {
            if (this === event.target) {
                if (this.id === 'navigation-panel' || MediaQuery.upTo(context.options.mobileBurgerSize)) {
                    context._enableBlur();
                }
                if (this.id === 'navigation-panel') {
                    context._toggleHeaderIconsVisibility(false);
                }
            }
        });
        $('#navigation-panel, #variable-header-container').on('animateOut.zf.toggler.blur', function(event) {
            if (this === event.target) {
                context._disableBlur();
                if (this.id === 'navigation-panel') {
                    context._toggleHeaderIconsVisibility(true);
                }
            }
        });

        // if the burger closes on mobile (light header), close nav panel and search panel too
        $('#variable-header-container').on('animateOut.zf.toggler', function(startEvent) {
            if (this !== startEvent.target) {
                return;
            }
            const $search = $('#navigation-search');

            // on desktop, we want the search panel to close with a transition
            if (!context._isMobile()) {
                if ($search.data('zfPlugin').isOn) {
                    $search.foundation('toggle');
                }
            }

            // on mobile, we don't do a transition and hide the search once the burger is closed
            // (hiding doesn't hurt for desktop, too here)
            $('#variable-header-container').one('off.zf.toggler', function(event) {
                if (this === event.target) {
                    $search.foundation('hide');
                    // make the blur vanish to allow scrolling
                    context._disableBlur();
                }
            });
        });

        // If the mobile navigation panel gets hidden, close every panel inside of it
        $('#variable-header-container').on('off.zf.toggler', function(event) {
            if (this === event.target) {
                $('.variable-header-item [data-toggler]', this).each((i, elem) => {
                    const $elem = $(elem);
                    const toggle = $elem.data('zfPlugin');

                    if (toggle.isOn) {
                        $elem.foundation('hide');
                    }
                });
            }
        });

        // the wrapper div of the search button
        const searchButtonWrapperElem = document.querySelector('.variable-header-item.item-search');

        // opening search
        $('#navigation-search').on('animateIn.zf.toggler', (event) => {
            if (MediaQuery.upTo(this.options.mobileBurgerSize)) {
                // make sure the burger is open, if we are on mobile
                // (e.g. when we came here via skip-link)
                const panel = $('#variable-header-container:hidden');
                if (panel.length > 0) {
                    panel.foundation('show');
                }
            } else {
                this._enableBlur();
                // make sure the burger is closed, if we are on desktop
                const panel = $('#navigation-panel:visible');
                if (panel.length > 0) {
                    panel.foundation('hide');
                }
            }
        });

        // after opening search
        $('#navigation-search').on('on.zf.toggler', (event) => {
            if (MediaQuery.upTo(this.options.mobileBurgerSize)) {
                // set every sibling of the search button inert
                this._toggleSiblingsInert(searchButtonWrapperElem, true);
            } else {
                // hide navigation options that should not be reachable while search is open
                this._toggleHeaderIconsVisibility(false);
            }
        });

        // closing search
        $('#navigation-search').on('animateOut.zf.toggler', (event) => {
            this._disableBlur();
            // show other navigation options and make them clickable again
            this._toggleHeaderIconsVisibility(true);
            this._toggleSiblingsInert(searchButtonWrapperElem, false);
        });

        // listen to open/close events on navigation panels and fire a custom event on the document for the burger
        $('#navigation-panel, #variable-header-container').on('animateIn.zf.toggler off.zf.toggler', (event) => {
            if (event.target !== event.currentTarget) return;
            const eventName = (event.type === 'animateIn') ? 'open.tud.navigation-panel' : 'close.tud.navigation-panel';
            document.dispatchEvent(new CustomEvent(eventName));
        });
    },

    /**
     * Fix (screen reader) usability issues in the navigation:
     *  - focus fix: focus first element in dropdown
     *
     * @private
     */
    _initFlyoutScreenReadability() {
        $('#portal-header .nav-button button').click((e) => {
            const $current = $(e.currentTarget);
            if ($current.attr('aria-expanded') === 'false') {
                // flyout is opening
                const $flyout = this._findFlyoutForLink($current);

                // Focus first element once the animation finished (focusing before could affect the animation).
                // Exclude navigation panels as they handle focus by themselves.
                if (['navigation-panel', 'variable-header-container'].indexOf($flyout.attr('id')) === -1) {
                    $flyout.one(transitionend($flyout), () => {
                        $('input, a, button', $flyout).not(':hidden').first().focus();
                    });
                }
            } else {
                // flyout is closing
                // set focus on the flyout link (needs timeout to stick)
                setTimeout(() => {
                    $current.focus();
                }, 50);
            }
        });
    },

    /**
     * find the corresponding nav-button for the given panel wrapped as jquery object
     */
    _findLinkForFlyout($flyout) {
        // each flyout is associated with a link that opens / closes it, referenced via data-toggle
        return $(`header #portal-header .nav-button button[data-toggle=${$flyout.attr('id')}]`);
    },

    /**
     * find the corresponding flyout for the given flyout link wrapped as jquery object
     */
    _findFlyoutForLink($link) {
        // each flyout is associated with one or more id(s) that opens / closes it, referenced via data-toggle
        const ids = $link.attr('data-toggle').split(' ');
        const idSelector = ids.map((id) => `#${id}`).join(', ');
        return $(idSelector);
    },

    /**
     * Set or unset siblings of a given HTMLElement inert (inactive for mouse and keyboard)
     * @param {HTMLElement} element - the HTML element whose siblings should get (not) inert
     * @param {Boolean} on=null - whether inert should be activated or deactivated, toggles if on=null
     */
    _toggleSiblingsInert(element, on = null) {
        const headerSiblings = element
            ? Array.from(element.parentNode.children).filter((sibling) => sibling !== element)
            : [];

        if (headerSiblings.length === 0) {
            return;
        }

        // if on parameter is not set, determine current state and toggle
        let nextOn = on;
        if (typeof nextOn !== 'boolean') {
            nextOn = !headerSiblings[0].inert;
        }

        headerSiblings.forEach((siblingElem) => {
            if (nextOn) {
                siblingElem.setAttribute('inert', '');
            } else {
                siblingElem.removeAttribute('inert');
            }
        });
    },

    /**
     * Enable blurring parts of the screen. By changing the mode, it is determined which part of the
     * site should remain clear (or not blurred).
     */
    _enableBlur() {
        // if blur is already enabled, do nothing
        if (document.body.classList.contains('blur-active')) {
            return;
        }

        // remember the current body width
        const bodyWidth = document.body.clientWidth;

        // calculate the current scroll position, activate the blur and move the body to the scroll position
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        document.body.classList.add('blur-active');
        document.body.style.top = `-${scrollY}px`;

        // there is no scrollbar anymore, add some padding with the width of the scrollbar to prevent body content
        // from "moving"
        const scrollbarWidth = document.body.clientWidth - bodyWidth;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.getElementById('header').style.paddingRight = `${scrollbarWidth}px`;

        this._toggleSiblingsInert(document.getElementById('header'), true);
    },

    /**
     * Disable blurring
     */
    _disableBlur() {
        // if blur is already disabled, do nothing
        if (!document.body.classList.contains('blur-active')) {
            return;
        }

        // get the "top shift" of the body, deactivate the blur, scroll to the current position
        const scrollPosition = Math.abs(parseInt(document.body.style.top || '0'));
        document.body.classList.remove('blur-active');
        window.scrollTo(0, scrollPosition);

        // remove the padding that replaced the scrollbar before
        document.body.style.paddingRight = null;
        document.getElementById('header').style.paddingRight = null;

        this._toggleSiblingsInert(document.getElementById('header'), false);
    },

});

// Register the plugins events
let $navPrimary = null;
App.registerUiComponent('navigation', () => {
    $navPrimary = $('#portal-header').find('#navigation');

    // initialize if not happened yet
    if (!$navPrimary.data('tud.navigation')) {
        $navPrimary.navigation();
    }
});
